import React, { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

// Constants
import { acceptedProfilePictureFormats } from 'Shared/constants/constants';

// Hooks
import useUpdateQuickStartGuideFlag from 'Dashboard/components/QuickStartGuide/useUpdateQuickStartGuideFlag';

// Helpers
import { convertFileToBase64 } from 'Shared/helpers';

// Types
import { User } from 'Shared/constants/types';
import { QuickStartGuideItemIDs } from 'Dashboard/components/QuickStartGuide/QuickStartGuideItems.constants';

// Actions
import * as UsersActions from 'Shared/actions/Users.actions';

// Components
import { WpModal, WpModalHeader, WpModalBody, WpModalFooter } from 'Wpds/components/modal';
import WpAvatar from 'Wpds/components/avatar/WpAvatar';
import { WpButtonGroup, WpButton } from 'Wpds/components/button';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { VisuallyHidden } from 'Wpds/components/visually-hidden/VisuallyHidden';

// Styles
import './EditUserAvatarModal.scss';

type Props = {
  user: User;
  isOpen: boolean;
  toggleIsOpen: () => void;
};

const EditUserAvatarModal = ({ user, isOpen, toggleIsOpen }: Props) => {
  const dispatch = useDispatch();
  const fileUploader = useRef(null);
  const { handleCompleteItem } = useUpdateQuickStartGuideFlag();

  const [isSaving, setIsSaving] = useState(false);
  const [previewAvatar, setPreviewAvatar] = useState(null);
  const [newAvatarFile, setNewAvatarFile] = useState(null);

  let avatarToPreview = { color: user.avatar.color, initials: 'xx' };

  if (previewAvatar) {
    avatarToPreview = { ...avatarToPreview, ...{ profilePictureUrl: previewAvatar } };
  }

  const handleChangePic = useCallback(async event => {
    const file = event.target.files[0];
    const url = await convertFileToBase64(file);
    setPreviewAvatar(url);
    setNewAvatarFile(file);
  }, []);

  const handleFileUploadClick = useCallback(() => fileUploader.current.click(), [fileUploader]);

  const closeModal = () => {
    setPreviewAvatar(null);
    setNewAvatarFile(null);
    toggleIsOpen();
  };

  const handleSaveClick = () => {
    setIsSaving(true);
    dispatch(UsersActions.updateUserPhoto(newAvatarFile)).then(() => {
      setIsSaving(false);
      handleCompleteItem(QuickStartGuideItemIDs.updateYourProfile);
      closeModal();
      // TODO: (josh) - this really shouldn't reload the page
      // but set and pull correctly in redux - reloading the page
      // due to avatars coming from multiple places in redux, this will
      // ensure they all get updated correctly
      window.location.reload();
    });
  };

  const baseClassName = 'EditUserAvatarModal';

  return (
    <WpModal isOpen={isOpen} size="sm" onToggle={closeModal}>
      <WpModalHeader>Profile Photo</WpModalHeader>
      <WpModalBody>
        <div className={baseClassName}>
          {previewAvatar ? (
            <WpAvatar avatar={avatarToPreview} size="xxxl" />
          ) : (
            <WpAvatar avatar={user.avatar} size="xxxl" />
          )}
          <div className={`${baseClassName}__button`}>
            <WpButton variant="tertiary" onClick={handleFileUploadClick} disabled={isSaving}>
              <WpIcon name="upload" />
              Upload Photo
            </WpButton>
          </div>
        </div>
        <VisuallyHidden>
          <input
            type="file"
            accept={acceptedProfilePictureFormats}
            name="avatar"
            ref={fileUploader}
            onChange={handleChangePic}
          />
        </VisuallyHidden>
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          <WpButton variant="tertiary" onClick={closeModal} disabled={isSaving}>
            Cancel
          </WpButton>
          <WpButton onClick={handleSaveClick} loading={isSaving} disabled={!previewAvatar}>
            Save
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
};

export default EditUserAvatarModal;
